import { BaseElement } from '../base-element';
import { AfterViewInit, Component, EventEmitter, forwardRef, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, NG_VALUE_ACCESSOR, ValidatorFn } from '@angular/forms';
import { FileItem, FileUploader, ParsedResponseHeaders } from 'ng2-file-upload';
import { SessionTokenService } from '../../../../session/service/session-token';
import { HttpConfig } from '../../../http/config';
import { ToastrService } from 'ngx-toastr';
import { FormEvent } from '../../../crud/form/form-event.enum';
import { IndirectUploadZoneConfig } from './indirect-upload-zone.config';
import { mergeMap, tap } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { HttpClient, HttpBackend, HttpHeaders } from '@angular/common/http';
import * as SparkMD5 from 'spark-md5';
import { Md5Service } from '../../../misc/service/md5-service';
import { ErrorCodes } from '../../../enum/api-errors';

@Component({
  selector: 'indirect-upload-zone',
  template: '<ng-content></ng-content>',
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => IndirectUploadZoneComponent),
    multi: true,
  }],
})
export class IndirectUploadZoneComponent extends BaseElement<string> implements OnInit, AfterViewInit {

  private httpClientNoIntercept: HttpClient;

  job: any;
  progress: number = 0;
  uploader: FileUploader;
  hasBaseDropZoneOver: boolean = false;
  uploadElRef: HTMLInputElement;
  uploading: boolean = false;
  signature: string = '';
  private apiResponse: any;

  @Input() formControlName: string;
  @Input() config: IndirectUploadZoneConfig;
  @Input() file: any;
  @Input() allowedExtensions: string[];
  @Output() readyForUpload = new EventEmitter<boolean>();
  private control: AbstractControl;
  private validator: ValidatorFn;
  private fileExtension: string;
  private fileBlob: any;


  constructor(private sessionTokenService: SessionTokenService,
              private httpConfig: HttpConfig,
              private httpClient: HttpClient,
              private toastr: ToastrService,
              private handler: HttpBackend) {
    super();
    this.httpClientNoIntercept = new HttpClient(handler);
  }

  ngAfterViewInit(): void {
    if (this.channel) {
      this.channel.subscribe((event: FormEvent) => {
        if (event === FormEvent.SUBMIT) {
          if (this.file) {
            this.form.controls[this.formControlName].setErrors(null);
          }
          if (this.form.controls[this.formControlName].invalid) {
            this.toggleInvalidClass(true);
          } else {
            this.toggleInvalidClass(false);
          }
        }
      });
    }
  }

  writeValue(value: string): void {
    this.innerValue = value;
    if (!this.file) {
      this.file = value;
    }
  }

  ngOnInit(): void {
    this.control = this.form.get(this.formControlName);
    // this.control = this.form.controls[this.formControlName];
    this.validator = this.control.validator;
    this.uploader = new FileUploader({});

    this.control.valueChanges.subscribe(
      (value: any) => {
        if (value && value.id) {
          this.file = value;
          this.control.setValue(value.id, { emitEvent: false });
        } else if (typeof value === 'string') {
          this.file = value;
          this.control.setValue(value, { emitEvent: false });
        }
      },
    );
  }

  onChange(): void {
    this.toggleInvalidClass(false);
  }

  fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
  }

  fileDropped(e: any, ref: HTMLInputElement): void {
    this.uploadElRef = ref;
    this.uploadIndirect(e[0], e[0].name);
  }

  fileSelected(e: any, ref: HTMLInputElement): void {
    this.uploadElRef = ref;
    this.uploadIndirect(e[0], e[0].name);
  }

  protected toggleInvalidClass(flag: boolean): void {
    this.customClass = Object.assign(this.customClass, { 'is-invalid': flag });
  }

  private uploadIndirect(file: File, name?: string) {
    if (file.size > 150 * 1_000_000) {
      this.toastr.error('File too large');
      return;
    }
    this.fileExtension = name.split('.').pop();
    this.validateExtension();
    this.fileBlob = file;
    this.uploading = true;
    let md5sum;

    this.httpClient.post<any>(this.config.url, { extension: this.fileExtension })
      .pipe(
        tap(response => {
          this.apiResponse = response;
        }),
        mergeMap((resp) => {
          const headers = new HttpHeaders({
            'Content-Type': this.getMimeType(),
          });
          return this.httpClientNoIntercept.put(resp.signedUrl, file, { headers })
            .pipe(
              mergeMap((s3Response: any) => {
                return from(Md5Service.compute(file)).pipe(
                  mergeMap((hash: any) => {
                    md5sum = hash;
                    return of(null);
                  }));
              }),
              mergeMap(() => {
                return this.httpClient.put<any>(
                  this.config.url,
                  {
                    ...resp,
                    originalFileName: name,
                    fileType: this.config.fileType,
                    extension: this.fileExtension,
                    md5sum: md5sum,
                    relatedTo: this.config.relatedTo,
                    aspectRatio: this.config.aspectRatio,
                  },
                );
              }),
            );
        }),
      )
      .subscribe(
        (data) => {
          this.onFileUpload(data);
          this.uploading = false;
        },
        error => {
          let message: string;
          if (error.error && error.error.message) {
            message = error.error.message;
          }
          if (error.error && error.error.code) {
            message = ErrorCodes[error.error.code] || error.error.code;
          }
          this.toastr.error(message || 'Upload error');
        },
      );
  }

  private onFileUpload(file: any) {
    this.control.clearValidators();
    this.control.setValidators(this.validator);
    this.control.setValue(file);

    this.onChange();

    if (typeof this.config.onFileUploaded === 'function') {
      this.config.onFileUploaded(file, this.formControlName, this.apiResponse, this.fileBlob);
    }

  }

  private getMimeType() {
    switch (this.fileExtension.toLowerCase()) {
      case 'mp4':
        return 'video/mp4';
      case 'webm':
        return 'video/webm';
      case 'm4a':
        return 'audio/mp4';
      case 'mp3':
        return 'audio/mpeg';
      case 'jpeg':
      case 'jpg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      case 'gif':
        return 'image/gif';
      case 'ttf':
        return 'font/ttf';
      default:
        this.toastr.error('Wrong file extension');
        throw new Error('wrong file extension');
    }
  }

  private validateExtension() {
    const fileExtension = this.fileExtension.toLowerCase();

    if (this.allowedExtensions && this.allowedExtensions.length) {
      if (this.allowedExtensions.indexOf(fileExtension) === -1) {
        this.toastr.error('Wrong file type');
        throw new Error('Wrong file type');
      }
      return;
    }

    const fileType = this.config.fileType;
    const defaultExtensions: { [key: string]: string[] } = {
      'image': ['jpg', 'jpeg', 'png', 'gif'],
      'video': ['mp4', 'webm'],
      'audio': ['mp3', 'm4a', 'mp4'],
    };

    if (defaultExtensions[fileType] && defaultExtensions[fileType].indexOf(fileExtension) === -1) {
      this.toastr.error('Wrong file type');
      throw new Error('Wrong file type');
    }
  }

}


